import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, ChatCircleDots, CaretDown, CaretRight } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/relay-history", label: "Relay History", icon: <ChatCircleDots size={18} weight="duotone" />, testId: "nav-admin-relay-history" }];

export default function AdminRelayHistoryPage() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data.filter((u) => u.role === "affiliate" || u.role === "manager"))).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (userId && userId !== "all") params.user_id = userId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/admin/relay/history", { params })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  const submit = (e) => { e.preventDefault(); load(); };

  const groups = [];
  const seenBatches = new Set();
  const repliesByParent = {};
  rows.forEach((r) => {
    if (r.thread_of) {
      if (!repliesByParent[r.thread_of]) repliesByParent[r.thread_of] = [];
      repliesByParent[r.thread_of].push(r);
    }
  });
  const threadReplyIds = new Set(rows.filter((r) => r.thread_of).map((r) => r.id));
  rows.forEach((r) => {
    if (threadReplyIds.has(r.id)) return; // rendered nested under its parent below, not as its own row
    if (r.batch_id) {
      if (seenBatches.has(r.batch_id)) return;
      seenBatches.add(r.batch_id);
      groups.push({ type: "batch", batch_id: r.batch_id, members: rows.filter((x) => x.batch_id === r.batch_id), message: r.message, from_name: r.from_name, created_at: r.created_at });
    } else {
      groups.push({ type: "single", row: r });
    }
  });

  return (
    <DashboardLayout navItems={navItems} moduleName="Relay Message History" moduleBackPath="/admin">
      <div data-testid="admin-relay-history-page">
        <div className="mb-6">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Every message Nia has relayed between reps, managers, and leads — search by person, date, or keyword.">Relay Message History</h1>
          <p className="text-igc-muted text-sm">Every Nia relay message ever sent — search by rep/manager, date, or keyword. Team broadcasts show who has reacted.</p>
        </div>

        <form onSubmit={submit} className="flex flex-wrap gap-3 mb-6 bg-igc-surface border border-igc-gold/15 p-4">
          <Input data-testid="relay-history-search-input" placeholder="Search keyword..." title="Type a keyword to filter relay messages by content." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream flex-1 min-w-[180px]" />
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="relay-history-user-select" title="Filter to messages sent to or from one specific rep/manager." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-[220px]">
              <SelectValue placeholder="Filter by rep/manager..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="relay-history-user-option-all">All Reps & Managers</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`relay-history-user-option-${u.id}`}>{u.display_name} ({u.user_id})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" data-testid="relay-history-date-from" title="Only show messages sent on or after this date." value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-[150px]" />
          <Input type="date" data-testid="relay-history-date-to" title="Only show messages sent on or before this date." value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-[150px]" />
          <Button data-testid="relay-history-search-button" type="submit" title="Applies your search and filters above." className="bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-1.5">
            <MagnifyingGlass size={14} /> Search
          </Button>
        </form>

        {loading && <p className="text-igc-muted text-sm" data-testid="relay-history-loading">Loading...</p>}
        {!loading && (
          <div className="space-y-2" data-testid="relay-history-results">
            {groups.length === 0 && <p className="text-igc-muted text-sm" data-testid="relay-history-empty">No messages found.</p>}
            {groups.map((g) =>
              g.type === "single" ? (
                <div key={g.row.id} className="bg-igc-surface border border-igc-gold/15 p-3" data-testid={`relay-history-row-${g.row.id}`}>
                  <div className="flex items-center justify-between text-xs text-igc-muted mb-1">
                    <span>{g.row.from_name} ({g.row.from_role}) → {g.row.to_name} ({g.row.to_role})</span>
                    <span>{formatET(g.row.created_at, "MMM d, yyyy h:mm a")}</span>
                  </div>
                  <p className="text-igc-cream text-sm">{g.row.message}</p>
                  <div className="flex gap-3 mt-1 text-[10px] uppercase tracking-wide">
                    <span
                      className={g.row.channel === "whatsapp" ? "text-igc-success" : g.row.channel === "email" ? "text-igc-goldText" : "text-igc-muted"}
                      data-testid={`relay-history-channel-${g.row.id}`}
                      title="How this message was delivered."
                    >
                      {g.row.channel === "whatsapp" ? "Via WhatsApp" : g.row.channel === "email" ? "Via Email" : "In-Portal"}
                    </span>
                    {g.row.reacted && <span className="text-igc-success" data-testid={`relay-history-reacted-${g.row.id}`}>Reacted</span>}
                    {g.row.replied && <span className="text-igc-goldText" data-testid={`relay-history-replied-${g.row.id}`}>Replied</span>}
                  </div>
                  {repliesByParent[g.row.id]?.map((rep) => (
                    <div key={rep.id} className="mt-2 pl-3 border-l-2 border-igc-gold/25" data-testid={`relay-history-thread-reply-${rep.id}`}>
                      <div className="text-igc-muted text-[11px] mb-0.5">↳ {rep.from_name} replied · {formatET(rep.created_at, "MMM d, h:mm a")}</div>
                      <p className="text-igc-cream text-sm">{rep.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div key={g.batch_id} className="bg-igc-surface border border-igc-gold/25" data-testid={`relay-history-batch-${g.batch_id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [g.batch_id]: !e[g.batch_id] }))}
                    className="w-full flex items-center justify-between p-3 text-left"
                    data-testid={`relay-history-batch-toggle-${g.batch_id}`}
                  >
                    <div>
                      <p className="text-igc-cream text-sm">Team Broadcast from {g.from_name} — {g.members.length} recipients</p>
                      <p className="text-igc-muted text-xs mt-0.5">{g.message}</p>
                    </div>
                    <div className="flex items-center gap-2 text-igc-goldText text-xs shrink-0" data-testid={`relay-history-batch-reacted-count-${g.batch_id}`}>
                      {g.members.filter((m) => m.reacted).length}/{g.members.length} reacted
                      {expanded[g.batch_id] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    </div>
                  </button>
                  {expanded[g.batch_id] && (
                    <div className="border-t border-igc-gold/10 divide-y divide-igc-gold/10">
                      {g.members.map((m) => (
                        <div key={m.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`relay-history-batch-member-${m.id}`}>
                          <span className="text-igc-cream">{m.to_name} ({m.to_role})</span>
                          <span className={m.reacted ? "text-igc-success" : "text-igc-muted"}>{m.reacted ? "✓ Reacted" : "No reaction yet"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
